import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

export interface EmploymentPeriod {
  startDate: Date
  endDate?: Date | null
  isUnemployment?: boolean
}

export interface EmploymentGap {
  startDate: Date  // used for matching which experience this gap follows
  endDate: Date    // used for matching
  displayStartDate: Date
  displayEndDate: Date
  duration: number
  durationString: string
}

type DatedEmploymentPeriod = EmploymentPeriod & { startDate: Date }

/**
 * Helper function to validate required parameters
 */
const validateRequiredParams = (functionName: string, params: unknown[]) => {
  for (const param of params) {
    if (param === undefined || param === null) {
      const error = new Error(
        `[${functionName}] Required parameter is undefined or null`
      )
      console.error(error)
      throw error
    }
  }
}

/** Check if a value is a valid Date object. */
const isValidDate = (date: unknown): date is Date =>
  date instanceof Date && !Number.isNaN(date.getTime())

/** Coerce GraphQL DateTime (runtime Date, typed as string) into a Date. */
export const toEmploymentDate = (value: unknown): Date | null => {
  if (isValidDate(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

/** Check if a period has a valid start date. */
const hasStartDate = (
  period: EmploymentPeriod | null | undefined
): period is DatedEmploymentPeriod => isValidDate(period?.startDate)

/**
 * Helper function to safely create dayjs objects from potentially invalid dates
 */
const safeDayjs = (
  date: Date | null | undefined,
  fallback?: dayjs.Dayjs
) => {
  if (!date) {
    return fallback || dayjs()
  }
  const parsed = dayjs(date)
  return parsed.isValid() ? parsed : fallback || dayjs()
}

/**
 * Converts a duration in months to a human-readable string
 */
const formatDuration = (durationMonths: number): string => {
  try {
    if (durationMonths >= 12) {
      return (durationMonths / 12).toFixed(1) + ' years'
    } else if (durationMonths < 1) {
      return '< 1 month'
    } else if (durationMonths === 1) {
      return '1 month'
    } else {
      return Math.round(durationMonths) + ' months'
    }
  } catch (error) {
    console.error('[formatDuration] Error formatting duration:', error)
    throw error
  }
}

/**
 * Merges overlapping employment periods into a single continuous period
 */
const mergeOverlappingPeriods = (
  periods: EmploymentPeriod[]
): EmploymentPeriod[] => {
  try {
    validateRequiredParams('mergeOverlappingPeriods', [periods])

    if (periods.length === 0) return []

    // Sort periods by start date
    const sortedPeriods = [...periods].sort(
      (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf()
    )

    const merged: EmploymentPeriod[] = []
    let current = { ...sortedPeriods[0] }

    for (let i = 1; i < sortedPeriods.length; i++) {
      const next = sortedPeriods[i]
      const currentEnd = current.endDate ? safeDayjs(current.endDate) : dayjs()
      const nextStart = dayjs(next.startDate)

      // Check if periods overlap or are adjacent (within 1 month)
      if (nextStart.isBefore(currentEnd.add(1, 'month'))) {
        // Merge periods - extend current end date to the later of the two end dates
        const nextEnd = next.endDate ? safeDayjs(next.endDate) : dayjs()

        // If either period is current (endDate: null), the merged period should be current
        if (!current.endDate || !next.endDate) {
          // Keep as current employment (endDate: null)
          current.endDate = null
        } else if (nextEnd.isAfter(currentEnd)) {
          // Both periods have end dates, use the later one
          current.endDate = next.endDate
        }
      } else {
        // No overlap, push current and start new period
        merged.push(current)
        current = { ...next }
      }
    }

    merged.push(current)
    return merged
  } catch (error) {
    console.error('[mergeOverlappingPeriods] Error merging periods:', error)
    throw error
  }
}

/**
 * Calculates the gap between two specific dates
 */
export const calculateEmploymentGap = (
  startDate: Date,
  endDate: Date
): EmploymentGap => {
  try {
    validateRequiredParams('calculateEmploymentGap', [startDate, endDate])

    // Use UTC to stay consistent with the display-date logic in
    // calculateEmploymentGaps. Mixing local and UTC month arithmetic caused
    // adjacent-month inputs to round to duration=1 in some timezones while
    // the display range inverted (ENG-3824).
    const endDateMonth = dayjs.utc(endDate).endOf('month')
    const startDateMonth = dayjs.utc(startDate).startOf('month')

    const duration = startDateMonth.diff(endDateMonth, 'month', true)
    const durationNumber = Math.max(0, Math.round(duration))

    return {
      startDate,
      endDate,
      displayStartDate: startDate,
      displayEndDate: endDate,
      duration: durationNumber,
      durationString: formatDuration(durationNumber),
    }
  } catch (error) {
    console.error('[calculateEmploymentGap] Error calculating gap:', error)
    throw error
  }
}

/**
 * Calculates all employment gaps in a list of employment experiences
 * Properly handles overlapping employment periods
 */
export const calculateEmploymentGaps = (
  experiences: EmploymentPeriod[]
): EmploymentGap[] => {
  try {
    validateRequiredParams('calculateEmploymentGaps', [experiences])

    const datedExperiences = experiences.filter(hasStartDate)
    if (datedExperiences.length === 0) return []

    // Merge overlapping employment periods
    const mergedPeriods = mergeOverlappingPeriods(datedExperiences)

    // Sort merged periods by start date (descending - most recent first)
    const sortedPeriods = mergedPeriods.sort(
      (a, b) => dayjs(b.startDate).valueOf() - dayjs(a.startDate).valueOf()
    )

    const gaps: EmploymentGap[] = []

    // Calculate gaps between consecutive merged periods
    for (let i = 0; i < sortedPeriods.length - 1; i++) {
      const currentPeriod = sortedPeriods[i]
      const nextPeriod = sortedPeriods[i + 1]

      if (nextPeriod.endDate) {
        // Adjusted display boundaries:
        // starts at beginning of the month after older job ended
        // ends at the month before newer job started
        // Use UTC to avoid timezone rollover when dates are
        // later displayed via getUtcMonth (which calls .utc().format)
        const displayStart = dayjs.utc(nextPeriod.endDate)
          .add(1, 'month')
          .startOf('month')
        const displayEnd = dayjs.utc(currentPeriod.startDate)
          .subtract(1, 'month')
          .endOf('month')

        const gap = calculateEmploymentGap(
          currentPeriod.startDate,
          nextPeriod.endDate
        )

        // Include any uncovered month between historical periods.
        // duration=0 means adjacent coverage (no missing month),
        // while duration=1 indicates a one-month gap.
        if (gap.duration >= 1) {
          gaps.push({
            ...gap,
            displayStartDate: displayStart.toDate(),
            displayEndDate: displayEnd.toDate(),
          })
        }
      }
    }
    return gaps
  } catch (error) {
    console.error('[calculateEmploymentGaps] Error calculating gaps:', error)
    throw error
  }
}

/**
 * Calculates summary statistics including employment gaps
 * Handles overlapping periods correctly
 */
export const calculateEmploymentSummary = (experiences: EmploymentPeriod[]) => {
  try {
    validateRequiredParams('calculateEmploymentSummary', [experiences])
    const employmentPeriods = experiences.filter(
      (exp): exp is DatedEmploymentPeriod =>
        !exp.isUnemployment && hasStartDate(exp)
    )

    if (employmentPeriods.length === 0) {
      return {
        totalMonthsExperience: 0,
        numberOfJobs: 0,
        averageDurationMonths: 0,
        longestTenureMonths: 0,
        totalGapMonths: 0,
        averageGapMonths: 0,
      }
    }

    // Calculate total experience from merged periods (to avoid double-counting overlaps)
    const mergedPeriods = mergeOverlappingPeriods(employmentPeriods)
    let totalMonthsExperience = 0

    mergedPeriods.forEach((period) => {
      const start = dayjs(period.startDate)
      const end = period.endDate ? safeDayjs(period.endDate) : dayjs()
      const duration = end.diff(start, 'month', true)
      totalMonthsExperience += duration
    })

    // Calculate individual job durations and longest tenure from original periods
    let longestTenureMonths = 0
    employmentPeriods.forEach((job) => {
      const start = dayjs(job.startDate)
      const end = job.endDate ? safeDayjs(job.endDate) : dayjs()
      const durationMonths = end.diff(start, 'month', true)

      if (durationMonths > longestTenureMonths) {
        longestTenureMonths = durationMonths
      }
    })

    // Calculate gaps
    const gaps = calculateEmploymentGaps(employmentPeriods)
    const totalGapMonths = gaps.reduce((sum, gap) => sum + gap.duration, 0)
    const averageGapMonths = gaps.length > 0 ? totalGapMonths / gaps.length : 0

    const averageDurationMonths =
      totalMonthsExperience / employmentPeriods.length

    return {
      totalMonthsExperience,
      numberOfJobs: employmentPeriods.length,
      averageDurationMonths,
      longestTenureMonths,
      totalGapMonths,
      averageGapMonths,
    }
  } catch (error) {
    console.error(
      '[calculateEmploymentSummary] Error calculating summary:',
      error
    )
    throw error
  }
}

/**
 * Checks for significant gaps at the beginning and end of employment history
 * Includes unemployment periods since FMCSA requires accounting for all time,
 * and documented unemployment satisfies this requirement.
 */
export const checkForSignificantGaps = (experiences: EmploymentPeriod[]) => {
  try {
    validateRequiredParams('checkForSignificantGaps', [experiences])

    const datedExperiences = experiences.filter(hasStartDate)

    if (datedExperiences.length === 0) {
      return { startGap: false, endGap: false }
    }

    const currentDate = dayjs.utc().startOf('month')

    // Include all experiences (employment AND unemployment) for gap checking
    // FMCSA requires accounting for all time - documented unemployment satisfies this

    // Find the latest effective end date and earliest start date across all
    // experiences. We must use the latest END date (not latest start date) to
    // detect the start-gap correctly — a job that started earlier but is still
    // current (endDate null) covers the period up to today.
    let latestEndDate: dayjs.Dayjs = dayjs.utc(0)
    let earliestStartDate: dayjs.Dayjs = dayjs.utc()

    for (const exp of datedExperiences) {
      const expEnd = isValidDate(exp.endDate)
        ? dayjs.utc(exp.endDate).startOf('month')
        : currentDate
      if (expEnd.isAfter(latestEndDate)) {
        latestEndDate = expEnd
      }

      const expStart = dayjs.utc(exp.startDate).startOf('month')
      if (expStart.isBefore(earliestStartDate)) {
        earliestStartDate = expStart
      }
    }

    const currentMonthGap = currentDate.diff(latestEndDate, 'month', true)

    // Check for gap before the 3-year FMCSA requirement
    const threeYearsAgo = currentDate.subtract(3, 'year')
    const fmcsaThresholdGap = earliestStartDate.diff(
      threeYearsAgo,
      'month',
      true
    )

    return {
      startGap: currentMonthGap >= 1,
      endGap: fmcsaThresholdGap > 0,
    }
  } catch (error) {
    console.error('[checkForSignificantGaps] Error checking gaps:', error)
    throw error
  }
}
